/**
 * tube（中心線＋半径の線状PZ、佐賀PZ-15など）の3D形状を、中心線に沿って
 * 半円の断面を掃引した三角形メッシュとして組み立てる（描画は tube-layer.ts）。
 *
 * ★ なぜfill-extrusionではないか: fill-extrusionは1枚のポリゴンを一様な高さで
 *   押し上げることしかできず、半円の天面は「幅方向に細切れにした帯の階段」で
 *   近似するしかなかった（輪切り20枚で約1,080枚・三角形約1万枚、半透明で
 *   帯の内壁が縞模様に透ける）。ridge-line-layer.ts冒頭の§164と同じ理由で、
 *   自前のメッシュにした。
 *
 * 断面は、水平半径＝radiusM、鉛直半径＝天井高（地表からの高さ×誇張倍率）の
 * 半楕円。誇張倍率を他のPZ（gv-pzsa-3d等の`pzsaCeilingExpression`）と揃える
 * ため、等倍以外では縦長の楕円になる。
 *
 * このファイルはmaplibre-glに依存しない純粋な計算だけを置く（テスト用）。
 */

/**
 * 頂点の位置は x・y＝正規化メルカトル[0,1]を`origin`からの差で、z＝メートル
 * （ridge-line-layer.tsの「落とし穴1.5」）。
 *
 * ★ なぜ差で持つか: メルカトル座標（0.86前後）をそのままFloat32Arrayに入れると
 *   約6e-8刻みに丸められ、地上で約2mの段差になる（幅110mの曲面が拡大時に
 *   ギザギザになる）。原点からの差なら値が小さく1mm未満の精度が保てる。
 *   原点のぶんの平行移動は、描画時に倍精度で行列へ畳み込む（tube-layer.ts）。
 */
export interface TubeMesh {
  /** 正規化メルカトルの原点（倍精度のまま持つ） */
  origin: [number, number];
  /** 3要素×頂点数（x・yはoriginからの差） */
  positions: Float32Array;
  /** 外向き法線（東・北・上の単位ベクトル）。陰影用 */
  normals: Float32Array;
  /** [r,g,b,a]×頂点数、各0〜1 */
  colors: Float32Array;
  vertexCount: number;
}

export interface TubeMeshInput {
  centerline: Array<{ lat: number; lon: number }>;
  radiusM: number;
  /** 天井（断面の頂点）の海抜高度 [m] */
  peakAltM: number;
  color: [number, number, number, number];
}

export interface TubeMeshOptions {
  /** 高さの誇張倍率（state.heightScale） */
  heightScale: number;
  /** その地点の地形標高 [m]（地形無効・未読込なら0） */
  groundElevation: (lon: number, lat: number) => number;
  /** 半円を何分割するか */
  radialSegments?: number;
}

const DEFAULT_RADIAL_SEGMENTS = 24;

/**
 * layers.tsの`metersPerDegree`と同じ式。2Dの帯（`tubeToPolygonCoords`）と
 * 3Dの筒の縁を一致させるため、必ず同じ式を使うこと（tube-mesh.test.tsで検証）。
 */
function metersPerDegree(lat: number): { lon: number; lat: number } {
  return { lon: 111320 * Math.cos((lat * Math.PI) / 180), lat: 110574 };
}

/** maplibregl.MercatorCoordinate.fromLngLat と同じ式 */
export function mercatorX(lon: number): number {
  return (180 + lon) / 360;
}

export function mercatorY(lat: number): number {
  return (180 - (180 / Math.PI) * Math.log(Math.tan(Math.PI / 4 + (lat * Math.PI) / 360))) / 360;
}

/** 区間の進行方向（東・北の単位ベクトル）。同一点が続く区間はnull */
function segmentDirection(
  a: { lat: number; lon: number },
  b: { lat: number; lon: number },
): [number, number] | null {
  const m = metersPerDegree((a.lat + b.lat) / 2);
  const e = (b.lon - a.lon) * m.lon;
  const n = (b.lat - a.lat) * m.lat;
  const len = Math.hypot(e, n);
  return len < 0.01 ? null : [e / len, n / len];
}

export function buildTubeMesh(tubes: TubeMeshInput[], options: TubeMeshOptions): TubeMesh {
  const radial = options.radialSegments ?? DEFAULT_RADIAL_SEGMENTS;
  const firstPoint = tubes.find((t) => t.centerline.length > 0)?.centerline[0];
  const origin: [number, number] = firstPoint
    ? [mercatorX(firstPoint.lon), mercatorY(firstPoint.lat)]
    : [0, 0];
  const positions: number[] = [];
  const normals: number[] = [];
  const colors: number[] = [];

  const push = (p: [number, number, number], nrm: [number, number, number], color: TubeMeshInput['color']) => {
    positions.push(p[0] - origin[0], p[1] - origin[1], p[2]);
    normals.push(nrm[0], nrm[1], nrm[2]);
    colors.push(color[0], color[1], color[2], color[3]);
  };

  for (const tube of tubes) {
    // 同一点の連続を除いた中心線と、各点の進行方向（前後の区間の平均）
    const pts = tube.centerline.filter(
      (p, i, arr) => i === 0 || segmentDirection(arr[i - 1], p) !== null,
    );
    if (pts.length < 2) continue;
    const segDirs = pts.slice(0, -1).map((p, i) => segmentDirection(p, pts[i + 1])!);
    const pointDirs = pts.map((_, i): [number, number] => {
      const prev = segDirs[i - 1];
      const next = segDirs[i];
      if (!prev) return next;
      if (!next) return prev;
      const e = prev[0] + next[0];
      const n = prev[1] + next[1];
      const len = Math.hypot(e, n);
      // 折り返し（180°）で平均が消える場合は先の区間に合わせる
      return len < 1e-6 ? next : [e / len, n / len];
    });

    // 各点の断面（半円）の頂点と法線
    const rings = pts.map((p, i) => {
      const [te, tn] = pointDirs[i];
      // 進行方向の左手側を+とする水平な横方向
      const le = -tn;
      const ln = te;
      const ground = options.groundElevation(p.lon, p.lat);
      const height = Math.max(tube.peakAltM - ground, 0) * options.heightScale;
      const m = metersPerDegree(p.lat);
      const ring: Array<{ pos: [number, number, number]; normal: [number, number, number] }> = [];
      for (let k = 0; k <= radial; k++) {
        const theta = (Math.PI * k) / radial;
        const c = Math.cos(theta);
        const s = Math.sin(theta);
        const offset = tube.radiusM * c;
        const lon = p.lon + (le * offset) / m.lon;
        const lat = p.lat + (ln * offset) / m.lat;
        // 楕円 (a·cosθ, b·sinθ) の外向き法線は (cosθ/a, sinθ/b) に比例する
        let ne = (c / tube.radiusM) * le;
        let nn = (c / tube.radiusM) * ln;
        let nu = height > 0 ? s / height : 1;
        const nlen = Math.hypot(ne, nn, nu);
        ne /= nlen;
        nn /= nlen;
        nu /= nlen;
        ring.push({ pos: [mercatorX(lon), mercatorY(lat), ground + height * s], normal: [ne, nn, nu] });
      }
      return { ring, ground, center: [mercatorX(p.lon), mercatorY(p.lat)] as [number, number] };
    });

    // 側面: 隣り合う断面どうしを三角形2枚ずつで結ぶ
    for (let i = 0; i < rings.length - 1; i++) {
      const a = rings[i].ring;
      const b = rings[i + 1].ring;
      for (let k = 0; k < radial; k++) {
        push(a[k].pos, a[k].normal, tube.color);
        push(b[k].pos, b[k].normal, tube.color);
        push(a[k + 1].pos, a[k + 1].normal, tube.color);
        push(a[k + 1].pos, a[k + 1].normal, tube.color);
        push(b[k].pos, b[k].normal, tube.color);
        push(b[k + 1].pos, b[k + 1].normal, tube.color);
      }
    }

    // 両端のふた（平らな半円）。法線は進行方向の外向き
    const caps: Array<{ r: (typeof rings)[number]; dir: [number, number]; sign: number }> = [
      { r: rings[0], dir: pointDirs[0], sign: -1 },
      { r: rings[rings.length - 1], dir: pointDirs[pointDirs.length - 1], sign: 1 },
    ];
    for (const { r, dir, sign } of caps) {
      const capNormal: [number, number, number] = [dir[0] * sign, dir[1] * sign, 0];
      const center: [number, number, number] = [r.center[0], r.center[1], r.ground];
      for (let k = 0; k < radial; k++) {
        push(center, capNormal, tube.color);
        push(r.ring[k].pos, capNormal, tube.color);
        push(r.ring[k + 1].pos, capNormal, tube.color);
      }
    }
  }

  return {
    origin,
    positions: new Float32Array(positions),
    normals: new Float32Array(normals),
    colors: new Float32Array(colors),
    vertexCount: positions.length / 3,
  };
}
